import _ from 'underscore';

import * as ArchiveActions from 'archive/ArchiveActions';
import {ArchiveStatus, NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BenchlingAction} from 'redux-stores/Action';
import {AssayRecordActionType} from 'results/assays/AssayRecordActions';
import {AssayRun} from 'results/assays/AssayRecordConstants';
import {Filter, FilterKey, keyToEmptyFilter, MultiValueFilter} from 'search/SearchConstants';

import {AvailabilityStatus, EntriesByRunId} from './RunsInboxModalConstants';
import {RunsInboxModalActionType} from './RunsInboxModalReduxActions';

export interface RunsInboxModalState {
  assayRunIds: Array<string>;
  entriesByRunId: EntriesByRunId;
  isLoading: boolean;
  isLoadingMore: boolean;
  offset: number;
  total: number;
  selectedRunId: string | null;
  filters: Array<Filter>;
  requestId: string | null;
  nextToken: string | null; // TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
  searchFilter: string; // TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
  statusFilter: AvailabilityStatus; // TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
  archiveFilter: ArchiveStatus; // TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
}
function getDefaultFilters(): Array<Filter> {
  const filters: Array<Filter> = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: [ModelTypeConstants.ASSAY_RUN],
    },
    {
      ...keyToEmptyFilter(FilterKey.RUN_AVAILABILITY),
      value: AvailabilityStatus.AVAILABLE,
    },
    {
      ...keyToEmptyFilter(FilterKey.ARCHIVE_PURPOSES),
      value: [NOT_ARCHIVED_PURPOSE_STRING],
    },
  ];
  return filters;
}
const RUNS_INBOX_MODAL_INITIAL_STATE: RunsInboxModalState = {
  assayRunIds: [],
  entriesByRunId: {},
  isLoading: true,
  isLoadingMore: false,
  offset: 0,
  total: 0,
  filters: getDefaultFilters(),
  selectedRunId: null,
  requestId: null,
  nextToken: null,
  searchFilter: '',
  statusFilter: AvailabilityStatus.AVAILABLE,
  archiveFilter: ArchiveStatus.NOT_ARCHIVED,
};

function reduceRunsInboxModal(
  state: RunsInboxModalState = RUNS_INBOX_MODAL_INITIAL_STATE,
  action: BenchlingAction
): RunsInboxModalState {
  // Using the non-Typescript AssayRecordAction actions with the Typescript RunsInboxModalActionType
  // actions in the same switch statement causes TS to error because it cannot resolve members of the unioned
  // BenchlingActions. Migrating AssayRecordAction is a lot of work so for now keep them separate.
  switch (action.type) {
    case AssayRecordActionType.ASSAY_RUN_SEARCH_STARTED:
      return {...state, assayRunIds: [], isLoading: true, requestId: action.requestId};
    case AssayRecordActionType.ASSAY_RUN_FETCH_MORE_RESULTS_STARTED:
      return {...state, isLoadingMore: true, requestId: action.requestId};
    // TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
    case AssayRecordActionType.LEGACY_ASSAY_RUN_SEARCH_COMPLETED: {
      if (action.requestId !== state.requestId) {
        return state;
      }
      const {assayRuns, entriesByRunId, nextToken} = action;
      const assayRunIds = assayRuns.map((assayRun: AssayRun) => assayRun.id);
      const newAssayRunIds = state.isLoadingMore
        ? _.unique([...state.assayRunIds, ...assayRunIds])
        : assayRunIds;
      return {
        ...state,
        assayRunIds: newAssayRunIds,
        entriesByRunId: {...state.entriesByRunId, ...entriesByRunId},
        selectedRunId: newAssayRunIds.includes(state.selectedRunId!) ? state.selectedRunId : null,
        isLoading: false,
        isLoadingMore: false,
        nextToken,
      };
    }
    case AssayRecordActionType.ASSAY_RUN_SEARCH_COMPLETED: {
      if (action.requestId !== state.requestId) {
        return state;
      }
      const {assayRuns, entriesByRunId, offset, total} = action;
      const assayRunIds = assayRuns.map((assayRun: AssayRun) => assayRun.id);
      const newAssayRunIds = state.isLoadingMore
        ? _.unique([...state.assayRunIds, ...assayRunIds])
        : assayRunIds;
      return {
        ...state,
        assayRunIds: newAssayRunIds,
        entriesByRunId: {...state.entriesByRunId, ...entriesByRunId},
        selectedRunId: newAssayRunIds.includes(state.selectedRunId!) ? state.selectedRunId : null,
        isLoading: false,
        isLoadingMore: false,
        offset,
        total,
      };
    }
    case ArchiveActions.ARCHIVE_RUNS_COMPLETED:
    case ArchiveActions.UNARCHIVE_RUNS_COMPLETED: {
      const {ids} = action;
      const changedRunIdsFromRunsInboxModal = ids.filter((id: string) => state.assayRunIds.includes(id));
      if (changedRunIdsFromRunsInboxModal.length) {
        const archived = action.type === ArchiveActions.ARCHIVE_RUNS_COMPLETED;
        let runStatusAndFilterMatch = false;
        if (BenchlingConfig.ENABLE_SIGHTGLASS_RUNS_SEARCH) {
          const currentArchiveFilter = state.filters.find(
            (filter) => filter.key === FilterKey.ARCHIVE_PURPOSES
          ) as MultiValueFilter;

          runStatusAndFilterMatch =
            currentArchiveFilter == null ||
            (archived && currentArchiveFilter.value.includes(action.purpose)) ||
            (!archived && currentArchiveFilter.value.includes(NOT_ARCHIVED_PURPOSE_STRING));
        } else {
          const currentArchiveFilter = state.archiveFilter;
          runStatusAndFilterMatch =
            (archived && currentArchiveFilter === ArchiveStatus.IS_ARCHIVED) ||
            (!archived && currentArchiveFilter === ArchiveStatus.NOT_ARCHIVED);
        }
        return {
          ...state,
          selectedRunId: null,
          assayRunIds: runStatusAndFilterMatch
            ? [...state.assayRunIds]
            : state.assayRunIds.filter((id) => !changedRunIdsFromRunsInboxModal.includes(id)),
        };
      }
      return {...state};
    }
    default: // Do nothing
  }

  switch (action.type) {
    case RunsInboxModalActionType.CHANGE_FILTERS: {
      const {filters} = action;
      return {...state, filters};
    }
    // TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
    case RunsInboxModalActionType.CHANGE_SEARCH_FILTER: {
      const {searchFilter} = action;
      return {...state, searchFilter};
    }
    // TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
    case RunsInboxModalActionType.CHANGE_STATUS_FILTER: {
      const {statusFilter} = action;
      return {...state, isLoading: true, statusFilter};
    }
    // TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
    case RunsInboxModalActionType.CHANGE_ARCHIVE_FILTER: {
      const {archiveFilter} = action;
      return {...state, archiveFilter};
    }
    case RunsInboxModalActionType.RESET_STATE: {
      return RUNS_INBOX_MODAL_INITIAL_STATE;
    }
    case RunsInboxModalActionType.SELECT_RUN: {
      const {runId} = action;
      return {...state, selectedRunId: runId};
    }
    default:
      return state;
  }
}

export default reduceRunsInboxModal;
